import { Card, CardContent, Dialog, Grid, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';

const lotTypeOption = [
  { value: 'TH', label: 'Townhome (TH)' },
  { value: 'SFR', label: 'Single-Family Residential (SFR)' },
];

const lotTypeCMOption = [
  { value: 'CM', label: 'Commercial (CM)' },
];

interface BuildingFloorUnitFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  isEnabled?: boolean;
  page?: string;
  syncWithDux: any;
  projectTypeValue: any;
  setAllBuildingLotsData: (value: any) => void;
}

export const BuildingFloorUnitForm = ({
  name,
  index,
  onRemove,
  isEnabled = false,
  page,
  syncWithDux,
  projectTypeValue,
  setAllBuildingLotsData,
}: BuildingFloorUnitFormProps) => {
  const { getValues, setValue, watch, setError, clearErrors } = useFormContext();
  const navigate = useNavigate();
  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.generateLotName(getValues('projectID')),
    isEnabled
  );

  const jobsiteRef = useRef<string | undefined>(getValues(`${name}.jobsite`));

  // ── Lot type tracking refs ────────────────────────────────────────────────
  // previousLotType: tracks the last-picked value — updated on every user change
  const previousLotType = useRef<string | null>(getValues(`${name}.lotType`) ?? null);
  // originalLotType: the server-saved value captured at mount — NEVER mutated during the session.
  // Reverting to this value silently resets the change flag without showing the dialog.
  const originalLotType = useRef<string | null>(getValues(`${name}.lotType`) ?? null);
  const lotTypeChangedRef = useRef<boolean>(false);

  // ── Lot type confirmation dialog state ────────────────────────────────────
  const [lotTypeChangeDialog, setLotTypeChangeDialog] = useState<{
    open: boolean;
    newValue: string;
    previousValue: string;
  }>({ open: false, newValue: '', previousValue: '' });

  useWatch({ name: `${name}.unitName` || [] });

  useEffect(() => {
    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    setAllBuildingLotsData(mergedLots);
  }, [getValues('projectBuildings')]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  const handleUnitNameChange = (value: string) => {
    let initialJobNumber = jobsiteRef.current || getValues(`${name}.jobsite`);
    if (!initialJobNumber) {
      initialJobNumber = '';
    }
    const newJobNumber = `${initialJobNumber}${value}`.trim();
    setValue(`${name}.jobsite`, newJobNumber);

    if (!value || value?.trim() === '') {
      clearErrors(`${name}.unitName`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.unitName`, { type: 'manual', message: 'Lot/Unit name already exists.' });
    } else {
      clearErrors(`${name}.unitName`);
    }
  };

  const handleJobnameNameChange = (value: any) => {
    jobsiteRef.current = value;

    if (!value || value?.trim() === '') {
      clearErrors(`${name}.jobsite`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobsite));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobsite`, { type: 'manual', message: 'Jobsite already exists.' });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: any) => {
    if (!value || value?.trim() === '') {
      clearErrors(`${name}.jobNumber`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobNumber`, { type: 'manual', message: 'Job number already exists.' });
    } else {
      clearErrors(`${name}.jobNumber`);
    }
  };

  const handleAddressChange = (value: any) => {
    if (!value || value?.trim() === '') {
      clearErrors(`${name}.address`);
      return;
    }

    const abbrError = getAddressAbbreviationError(value);
    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.address));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (abbrError) {
      setError(`${name}.address`, { type: 'manual', message: abbrError });
    } else if (isDuplicate) {
      setError(`${name}.address`, { type: 'manual', message: 'Address already exists.' });
    } else {
      clearErrors(`${name}.address`);
    }
  };

  // ── Lot type change handler ───────────────────────────────────────────────
  /**
   * previousLotType.current — last-picked value (updated on every change, including after dialog)
   * originalLotType.current  — server-saved value at mount (NEVER mutated during the session)
   *
   * Dialog rules:
   *   - same value as last pick                         → no-op, NO dialog
   *   - isUnitCreationCompleted = false / null          → unit not yet saved, NO dialog, silent apply
   *   - original was null (first-time pick)             → NO dialog, silent apply
   *   - new value === original (reverted to server val) → NO dialog, silent apply, reset flag to false
   *   - new value !== original AND unit already created → SHOW pricing confirmation dialog
   */
  const handleLotTypeChange = (newValue: string) => {
    const prevValue = previousLotType.current;  // null | string — last user pick
    const origValue = originalLotType.current;  // null | string — never changes

    const isSameAsPrevious = prevValue === newValue;
    // ✅ User picked back the original server-saved value → silent revert, no dialog
    const isRevertedToOriginal = !!origValue && origValue === newValue;

    // ✅ Only show the dialog when the unit is already saved to the server
    const isUnitCreationCompleted = getValues(`${name}.isUnitCreationCompleted`);

    if (isSameAsPrevious) {
      // Exact same as last pick → no-op
      return;
    }

    if (!isUnitCreationCompleted || !origValue || isRevertedToOriginal) {
      // Unit not yet created  → no dialog
      // Original was null     → first-time pick, no dialog
      // Reverted to original  → no dialog, clear the changed flag
      lotTypeChangedRef.current = false;
      previousLotType.current = newValue;
      setValue(`${name}.lotType`, newValue);
      // ✅ When reverting to original, reset both flags so submit sends false/false
      if (isRevertedToOriginal) {
        setValue(`${name}.lotTypeChanged`, false);
        setValue(`${name}.isApplyAppropriatePLConfirmed`, false);
      }
    } else {
      // Genuine change away from the original saved value → show dialog
      lotTypeChangedRef.current = true;
      setLotTypeChangeDialog({ open: true, newValue, previousValue: prevValue as string });
    }
  };

  /**
   * User clicked "Yes" — apply new lot type + mark pricing confirmed.
   * ✅ originalLotType is intentionally NOT updated here so that reverting
   *    back to the mount-time value later still suppresses the dialog.
   */
  const handleLotTypeConfirmYes = () => {
    const { newValue } = lotTypeChangeDialog;
    setValue(`${name}.lotType`, newValue);
    setValue(`${name}.isApplyAppropriatePLConfirmed`, true);
    setValue(`${name}.lotTypeChanged`, true);
    // Only previousLotType advances; originalLotType stays at the mount-time value.
    previousLotType.current = newValue;
    setLotTypeChangeDialog({ open: false, newValue: '', previousValue: '' });
  };

  /**
   * User clicked "No" — apply new lot type but skip pricing update.
   * ✅ originalLotType is intentionally NOT updated here for the same reason.
   */
  const handleLotTypeConfirmNo = () => {
    const { newValue } = lotTypeChangeDialog;
    setValue(`${name}.lotType`, newValue);
    setValue(`${name}.isApplyAppropriatePLConfirmed`, false);
    setValue(`${name}.lotTypeChanged`, true);
    // Only previousLotType advances; originalLotType stays at the mount-time value.
    previousLotType.current = newValue;
    setLotTypeChangeDialog({ open: false, newValue: '', previousValue: '' });
  };

  const projectSyncWithDUX = watch(`${name}.projectSyncWithDUX`);
  const isProjectTypeRES = true;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Card elevation={0} className="floorUnitBlockWrapper" style={{ marginBottom: '0', background: 'none' }}>
        <CardContent className="  " style={{ paddingTop: '4px', paddingBottom: '4px', paddingLeft: '4px' }}>
          <Grid container spacing={2}>
            <Grid item lg={8} sm={8} xl={8} xs={8}>
              <div style={{ display: 'none' }}>
                <label>{getValues(`${name}.unitNumber`)}</label>
              </div>
              <Grid container spacing={2}>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.8 : 2}
                  sm={isProjectTypeRES ? 1.8 : 2}
                  xl={isProjectTypeRES ? 1.8 : 2}
                  xs={isProjectTypeRES ? 1.8 : 2}
                >
                  <FormInput
                    name={`${name}.jobsite`}
                    label={'Jobsite'}
                    onChange={(e: any) => handleJobnameNameChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                        getValues('projectID'),
                        '',
                        getValues(`${name}.unitID`),
                        '',
                        value
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.7 : 2}
                  sm={isProjectTypeRES ? 1.7 : 2}
                  xl={isProjectTypeRES ? 1.7 : 2}
                  xs={isProjectTypeRES ? 1.7 : 2}
                >
                  <FormInput
                    name={`${name}.unitName`}
                    label="Lot/Unit"
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                        getValues('projectID'),
                        '',
                        getValues(`${name}.unitID`),
                        value,
                        ''
                      )
                    }
                    onChange={(e: any) => handleUnitNameChange(e)}
                  />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                  <FormInput
                    name={`${name}.jobNumber`}
                    label={'Builder Job Number'}
                    onChange={(e: any) => handleJobNumberChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                        getValues('projectID'),
                        '',
                        getValues(`${name}.unitID`),
                        '',
                        '',
                        value
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.5 : 2}
                  sm={isProjectTypeRES ? 1.5 : 2}
                  xl={isProjectTypeRES ? 1.5 : 2}
                  xs={isProjectTypeRES ? 1.5 : 2}
                >
                  <FormInput
                    name={`${name}.address`}
                    label="Address"
                    onChange={(e: any) => handleAddressChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                        getValues('projectID'),
                        value,
                        getValues(`${name}.unitID`),
                        '',
                        ''
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.5 : 2}
                  sm={isProjectTypeRES ? 1.5 : 2}
                  xl={isProjectTypeRES ? 1.5 : 2}
                  xs={isProjectTypeRES ? 1.5 : 2}
                >
                  <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                  <FormAutoCompleteSelectDynamic
                    name={`${name}.proctorResult`}
                    label="Proctor Result "
                    getQueryKeyConfig={() =>
                      proctorResultApiKeyStore.getProjectProctorResults(getValues('projectID'))
                    }
                    optionsValueKey="proctorResultsID"
                    optionsLabelKey="proctorFileName"
                  />
                </Grid>
                {isProjectTypeRES && (
                  <Grid item lg={1.5} sm={1.5} xl={1.5} xs={1.5}>
                    <FormSelect
                      name={`${name}.lotType`}
                      label="Lot Type"
                      options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                      error={true}
                      onChange={(e: any) => handleLotTypeChange(e)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item lg={4} sm={4} xl={4} xs={4}>
              <Grid
                container
                direction="row"
                sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
              >
                <FormCheckbox label="Create WO" name={`${name}.createWO`} />
                <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!projectSyncWithDUX} />
                <FormInput label="Building Permit#" name={`${name}.buildingPermit`} styleField={{ width: '200px' }} />
                {onRemove && index !== undefined && (
                  <div style={{}}>
                    {page && getValues(`${name}.isUnitCreationCompleted`) && !!getValues(`${name}.unitID`) && (
                      <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                        <IconButton
                          style={{ padding: '10px 2px 5px 15px' }}
                          aria-label="view"
                          onClick={() => navigate(`/units/profile/${getValues(`${name}.unitID`)}`)}
                        >
                          <Tooltip title={<Typography variant="body1">View</Typography>}>
                            <img src={Icons.IconUnitsLotsDetails} alt="View" />
                          </Tooltip>
                        </IconButton>
                      </GuardedComponent>
                    )}
                  </div>
                )}
                {!!getValues(`${name}.unitID`) && (
                  <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                    {onRemove &&
                      index !== undefined &&
                      (!getValues(`${name}.disableDelete`) ? (
                        <div style={{}}>
                          <DeleteButton
                            data={undefined}
                            deleteMutationConfig={() =>
                              projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))
                            }
                            onDeleted={() => onRemove(index)}
                          >
                            <>
                              <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                                <img src={Icons.Delete} alt="DELETE" />
                              </Tooltip>
                            </>
                          </DeleteButton>
                        </div>
                      ) : (
                        <div style={{}}>
                          <DeleteButton
                            data={undefined}
                            deleteMutationConfig={() =>
                              projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))
                            }
                            onDeleted={() => onRemove(index)}
                            isDisabled={true}
                          >
                            <>
                              <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                                <img src={Icons.DisabledDelete} alt="DELETE" />
                              </Tooltip>
                            </>
                          </DeleteButton>
                        </div>
                      ))}
                  </GuardedComponent>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── ✅ Lot Type Pricing Confirmation Dialog ──────────────────────────── */}
      {/* Only shown when isUnitCreationCompleted = true AND lot type changed from original */}
      {lotTypeChangeDialog.open && (
        <Dialog open={lotTypeChangeDialog.open} className="modalpopup">
          <div style={{ padding: '25px 50px', textAlign: 'center' }}>
            <Typography variant="body1">
              Do you want to update the pricing for this lot&apos;s work orders based on the selected lot type
              change?
            </Typography>
          </div>
          <DialogActionsBar>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', padding: '8px' }}>
              <button
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#0000b5', color: '#fff', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmYes}
              >
                Yes
              </button>
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{ backgroundColor: '#d7dbdd', color: 'black', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmNo}
              >
                No
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};